import type { ReactNode } from "react";

interface ScreenProps {
  readonly children: ReactNode;
  /** Pinned to the bottom on a phone; follows the content on a desktop. */
  readonly foot?: ReactNode;
  /** Centre the body both ways while it fits; scroll once it does not. */
  readonly centred?: boolean;
  /** Centre vertically but keep the content full-width and left-aligned. */
  readonly middle?: boolean;
  /** A thin progress rule across the top, 0–1. */
  readonly progress?: number;
}

/**
 * The one full-screen scaffold: a scrolling body with an optional foot.
 *
 * On a phone the body fills the screen and the foot is pinned, because a
 * bottom action and a centred body cannot both win a fight over the same
 * free space. On a desktop the same content sits in a 440px column in the
 * middle of the page — a reading column, not a phone stretched to the
 * edges — and the foot simply follows it.
 */
export function Screen({ children, foot, centred = false, middle = false, progress }: ScreenProps) {
  return (
    <div className="flex min-h-app flex-col bg-paper text-ink md:items-center md:justify-center md:px-8 md:py-16">
      <div className="flex w-full flex-1 flex-col md:w-[440px] md:flex-none">
        {progress !== undefined && (
          <div className="px-gutter pt-3 md:px-0" aria-hidden>
            <div className="h-px w-full bg-rule">
              <div
                className="h-px bg-ink transition-[width] duration-300"
                style={{ width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%` }}
              />
            </div>
          </div>
        )}
        <main
          className={
            centred
              ? "flex flex-1 flex-col items-center justify-center-safe px-gutter py-6 text-center md:px-0 md:py-10"
              : middle
                ? "flex flex-1 flex-col justify-center-safe px-gutter py-6 md:px-0 md:py-8"
                : "flex flex-1 flex-col px-gutter py-6 md:px-0 md:py-8"
          }
        >
          {children}
        </main>
        {foot && (
          // Pinned on a phone so the one action stays in reach however long the
          // page runs; on a desktop it simply follows the column.
          <footer className="sticky bottom-app flex flex-col gap-2 bg-paper px-gutter pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3 md:static md:px-0 md:pb-0 md:pt-6">
            {foot}
          </footer>
        )}
      </div>
    </div>
  );
}
